import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

// Animated number counter
export function Counter({ value = 0, suffix = "", duration = 1800 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = null;
    const target = Number(value) || 0;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setDisplay(target);
    };
    requestAnimationFrame(step);
  }, [inView, value, duration]);

  return (
    <span ref={ref}>
      {display.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
}

// Scroll reveal wrapper
export function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Section heading: mono overline with saffron square marker + heavy display title
export function SectionHeading({ overline, title, subtitle, light = false, center = false }) {
  return (
    <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} mb-12 md:mb-16`}>
      {overline && (
        <p className={`inline-flex items-center gap-2.5 font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] font-bold text-gold mb-5 ${center ? "justify-center" : ""}`}>
          <span className="h-2 w-2 bg-gold inline-block" />
          {overline}
        </p>
      )}
      <h2
        className={`font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-black leading-[1.05] ${
          light ? "text-white" : "text-navy"
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-5 text-base md:text-lg leading-relaxed ${light ? "text-slate-300" : "text-gray-600"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-gold animate-spin" />
    </div>
  );
}
